import get from 'lodash/get';

import { getIntercomJwt } from 'common/api/v1/intercom';

export const logoutActions = () => {
    stopIntercomIdentityRefresh();
    if (window.Intercom) {
        window.Intercom('shutdown');
    }
    localStorage.clear();
    window.location.replace('/login');
};

const INTERCOM_JWT_REFRESH_MS = 8 * 60 * 1000;
let intercomJwtRefreshTimer = null;

const fetchIntercomUserJwt = async () => {
    try {
        const { data } = await getIntercomJwt();
        return data?.data.token ?? null;
    } catch {
        // ignore — caller decides how to proceed without a verified identity
        return null;
    }
};

const bootIntercom = (session, intercomUserJwt) => {
    if (!window.Intercom || !import.meta.env.VITE_INTERCOM_APP_ID) return;
    const intercomInitObj = {
        app_id: import.meta.env.VITE_INTERCOM_APP_ID,
        api_base: import.meta.env.VITE_INTERCOM_API_BASE,
        created_at: Math.round(new Date().getTime() / 1000),
        email: session.email,
        name: session.first_name + ' ' + session.last_name,
        ...(intercomUserJwt ? { intercom_user_jwt: intercomUserJwt } : {}),
    };
    const intercomExtraData = session.ss_data ? session.ss_data : {};
    window.Intercom('boot', { ...intercomInitObj, ...intercomExtraData });
};

const stopIntercomIdentityRefresh = () => {
    if (intercomJwtRefreshTimer) {
        clearInterval(intercomJwtRefreshTimer);
        intercomJwtRefreshTimer = null;
    }
};

const startIntercomIdentityRefresh = () => {
    stopIntercomIdentityRefresh();
    intercomJwtRefreshTimer = setInterval(async () => {
        if (!window.Intercom) return;
        const session = getSessionLocalStorage();
        if (!session) return;

        const intercomUserJwt = await fetchIntercomUserJwt();
        if (!intercomUserJwt) return;
        window.Intercom('update', { intercom_user_jwt: intercomUserJwt });
    }, INTERCOM_JWT_REFRESH_MS);
};

export const initIntercomSession = async (session) => {
    if (!window.Intercom || !import.meta.env.VITE_INTERCOM_APP_ID) return;

    const intercomUserJwt = await fetchIntercomUserJwt();
    bootIntercom(session, intercomUserJwt);
    startIntercomIdentityRefresh();
};

export const initSessionActions = (setCurrentValues) => {
    const session = getSessionLocalStorage();
    if (session) {
        if (setCurrentValues) {
            setCurrentValues({
                first_name: session.first_name,
                last_name: session.last_name,
                profile_image: session.profile_image,
            });
        }

        initIntercomSession(session);
    }
};

export const getCountryCurrency = () => {
    const session = getSessionLocalStorage();
    return get(session, 'currency_company_locale', {});
};

export const getCountryCurrencyIso = () => {
    const currency = getCountryCurrency();
    if (currency) return get(currency, 'abbreviation', 'USD');
    return 'USD';
};

export const getCountryCurrencyLocale = () => {
    const currency = getCountryCurrency();
    if (currency) return get(currency, 'locale', 'en-US').replace(/_/g, '-');
    return 'en-US';
};

export const getCountryCurrencySymbol = () => {
    const currency = getCountryCurrency();
    if (currency) return get(currency, 'symbol', '$');
    return '$';
};

export const getSessionLocalStorage = () =>
    typeof localStorage.getItem('session') !== 'undefined'
        ? JSON.parse(localStorage.getItem('session'))
        : {};

export const getIsGuess = () => !localStorage.getItem('token');

export const getToken = () => localStorage.getItem('token');

export const getCompanyGroup = () => {
    const session = getSessionLocalStorage();
    return get(session, 'company_group', {});
};

export const getRoleLevel = () => {
    const companyGroup = getCompanyGroup();
    return get(companyGroup, 'owner_type', 0);
};

export const getIsOwnerRole = () => getRoleLevel() === 0;

export const getNumberFormatPartsByCountryCurrency = () =>
    new Intl.NumberFormat(getCountryCurrencyLocale(), {
        style: 'currency',
        currency: getCountryCurrencyIso(),
    }).formatToParts(Math.PI * 10000);

export const getCountryCurrencyDecimalSeparator = () => {
    const parts = getNumberFormatPartsByCountryCurrency();
    const decimalSeparator = parts.find((p) => p.type === 'decimal');
    if (decimalSeparator) return decimalSeparator.value;
    return '.';
};

export const getCountryCurrencyThousandSeparator = () => {
    const parts = getNumberFormatPartsByCountryCurrency();
    const thousandSeparator = parts.find((p) => p.type === 'group');
    if (thousandSeparator) return thousandSeparator.value;
    return ',';
};

export const updateSession = (props) => {
    const session = getSessionLocalStorage();
    if (session && props) {
        localStorage.setItem(
            'session',
            JSON.stringify(Object.assign(session, props)),
        );
    }
};
